import { create } from 'zustand';
import type { SystemAlert } from '@/types/raspi';
import { checkHubHealth } from '@/services/api';

// Helper function to normalize health status
const normalizeHealthStatus = (status: string): 'connected' | 'disconnected' | 'error' => {
  switch (status.toLowerCase()) {
    case 'connected':
    case 'running':
    case 'healthy':
      return 'connected';
    case 'disconnected':
    case 'stopped':
    case 'unknown':
      return 'disconnected';
    case 'error':
    case 'unhealthy':
    default:
      return 'error';
  }
};

interface SystemHealthStore {
  // System Health Data
  mqttStatus: 'connected' | 'disconnected' | 'error';
  websocketStatus: 'connected' | 'disconnected' | 'error';
  databaseStatus: 'connected' | 'disconnected' | 'error';
  hubUptime: number;
  lastHealthCheck: string;
  systemAlerts: SystemAlert[];

  // Store Management
  isLoading: boolean;
  error: string | null;
  healthCheckInterval: NodeJS.Timeout | null;

  // Actions
  fetchSystemHealth: () => Promise<void>;
  startHealthMonitoring: () => void;
  stopHealthMonitoring: () => void;
  updateHealthStatus: (service: string, status: string) => void;
  addSystemAlert: (alert: SystemAlert) => void;
  clearSystemAlerts: () => void;
  clearError: () => void;
  resetStore: () => void;
}

export const useSystemHealthStore = create<SystemHealthStore>((set, get) => ({
  // Initialize with default values
  mqttStatus: 'disconnected',
  websocketStatus: 'disconnected',
  databaseStatus: 'disconnected',
  hubUptime: 0,
  lastHealthCheck: new Date().toISOString(),
  systemAlerts: [],
  isLoading: false,
  error: null,
  healthCheckInterval: null,

  fetchSystemHealth: async () => {
    set({ isLoading: true, error: null });
    try {
      const healthData = await checkHubHealth();
      
      set({
        mqttStatus: normalizeHealthStatus(healthData.services?.mqtt?.status || 'disconnected'),
        websocketStatus: normalizeHealthStatus(healthData.services?.websocket?.status || 'disconnected'),
        databaseStatus: normalizeHealthStatus(healthData.services?.database?.status || 'disconnected'),
        hubUptime: typeof healthData.uptime === 'number' ? healthData.uptime : 0,
        lastHealthCheck: new Date().toISOString(),
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch system health',
        isLoading: false,
        mqttStatus: 'error',
        websocketStatus: 'error',
        databaseStatus: 'error',
      });
    }
  },

  startHealthMonitoring: () => {
    const { healthCheckInterval } = get();
    
    // Clear existing interval if any
    if (healthCheckInterval) {
      clearInterval(healthCheckInterval);
    }

    // Start health monitoring every 30 seconds
    const interval = setInterval(() => {
      get().fetchSystemHealth();
    }, 30000);

    set({ healthCheckInterval: interval });
    
    // Initial health check
    get().fetchSystemHealth();
  },

  stopHealthMonitoring: () => {
    const { healthCheckInterval } = get();
    
    if (healthCheckInterval) {
      clearInterval(healthCheckInterval);
      set({ healthCheckInterval: null });
    }
  },

  updateHealthStatus: (service: string, status: string) => {
    set(() => {
      const updates: Partial<SystemHealthStore> = {};
      
      switch (service) {
        case 'mqtt':
          updates.mqttStatus = normalizeHealthStatus(status);
          break;
        case 'websocket':
          updates.websocketStatus = normalizeHealthStatus(status);
          break;
        case 'database':
          updates.databaseStatus = normalizeHealthStatus(status);
          break;
      }
      
      return {
        ...updates,
        lastHealthCheck: new Date().toISOString(),
      };
    });
  },

  addSystemAlert: (alert: SystemAlert) => {
    set((state) => ({
      systemAlerts: [alert, ...state.systemAlerts].slice(0, 100), // Keep last 100 alerts
    }));
  },

  clearSystemAlerts: () => {
    set({ systemAlerts: [] });
  },

  clearError: () => {
    set({ error: null });
  },

  resetStore: () => {
    const { healthCheckInterval } = get();
    
    // Clear health monitoring interval
    if (healthCheckInterval) {
      clearInterval(healthCheckInterval);
    }

    set({
      mqttStatus: 'disconnected',
      websocketStatus: 'disconnected',
      databaseStatus: 'disconnected',
      hubUptime: 0,
      lastHealthCheck: new Date().toISOString(),
      systemAlerts: [],
      isLoading: false,
      error: null,
      healthCheckInterval: null,
    });
  },
}));

export default useSystemHealthStore; 